import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { TableProps } from '@kanda-libs/ks-component-ts';
import { FuseHit, StringIndexedObject } from '@kanda-libs/ks-design-library';

import usePagination from 'hooks/usePagination';

import { sortData } from './helpers';
import useUserTableSorting from './useUserTableSorting';

import { User } from '../UsersListPage/helpers';
import { ModalProps } from '../UsersListPage/useUsersListPage';

export interface UserTablePropsHook extends TableProps {}

export default function useUserTableProps(
  hits: FuseHit<User>[] = [],
  query = '',
  isLoading = false,
  onRowClick: (props: ModalProps) => void,
): UserTablePropsHook {
  const { sorting, order, onAction } = useUserTableSorting();

  const { pageIndex, perPage, totalPages, setPageIndex } = usePagination(
    hits.length,
  );

  const data = useMemo(() => {
    const rawData = hits.map((hit) => hit.item);
    return sortData(rawData, sorting, order);
  }, [order, sorting, hits]);

  // Data slicing is used in place of API pagination - in real use, use API
  // pagination
  const paginatedData = useMemo(() => {
    return (
      data?.slice(pageIndex * perPage, pageIndex * perPage + perPage) || []
    );
  }, [data, pageIndex, perPage]);

  const setPage = useCallback(
    (index: number) => setPageIndex(index),
    [setPageIndex],
  );

  useEffect(() => {
    setPage(0);
  }, [setPage]);

  const queryRef = useRef('');

  // Effect resets page index to 0 when query changes - when user searches
  useEffect(() => {
    if (queryRef.current && queryRef.current === query) return;
    setPageIndex(0);
  }, [query, setPageIndex]);

  const onRowClicked = useCallback(
    (row?: StringIndexedObject) => {
      if (row) {
        const { email, cid } = row.original;
        onRowClick({ email, cid });
      }
    },
    [onRowClick],
  );

  return {
    data: paginatedData,
    onRowClicked,
    onAction,
    pageIndex,
    totalPages,
    setPage,
    isLoading,
  };
}
